import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { getBookingSlot, getAllTicketGroupBySlug } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useNightMode } from "../../contexts/NightModeContext";
import { images } from "../../assets/images";
import { 
  LayoutDashboard,
  Ticket,
  Percent,
  BarChart3,
  Users,
  CalendarRange,
  LogOut,
  Palette
} from 'lucide-react';
import '../../components/layout/Organizer/SideNavMenu.css';

type SideNavMenuProps = {
  minifyNav: boolean;
  mobileNavOpen: boolean;
  setMobileNavBarOpen: (value: boolean) => void;
};

const UserEventSideNavMenu = (props: SideNavMenuProps) => {
  const { minifyNav, mobileNavOpen, setMobileNavBarOpen } = props;
  const location = useLocation();
  const { eventId } = useParams();
  const { slugId } = useParams();
  const [hasSlotData, setHasSlotData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const { isNightMode, toggleNightMode } = useNightMode();
  const { user } = useAuth();

  useEffect(() => {
    let links = document.querySelectorAll(".menu--link");
    let link = location.pathname;
    links.forEach((el) => {
      if (el.getAttribute("href") === link) {
        el.classList.add("active");
      } else {
        el.classList.remove("active");
      }
    });
  }, [location]);

  useEffect(() => {
    const checkSlotData = async () => {
      const eventData = localStorage.getItem("eventData");
      setIsLoading(true);
      try {
        if (eventData) {
          const eventSlug = JSON.parse(eventData).slugId;
          const data = await getBookingSlot(eventSlug);
          
          let ticketGroups = [];
          try {
            ticketGroups = await getAllTicketGroupBySlug(eventSlug);
          } catch (ticketError) {
            console.error("Failed to fetch ticket groups", ticketError);
          }

          const hasSlotRequiredGroup = Array.isArray(ticketGroups) && ticketGroups.some(
            (group: any) => group.isSlotRequired === true || group.isSlotRequired === "true"
          );

          setHasSlotData((data && data.length > 0) || hasSlotRequiredGroup);
        }
      } catch (err) {
        console.error("Failed to fetch slot data", err);
        setHasSlotData(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkSlotData();
  }, [slugId]);

  const getEventName = () => {
    const eventData = localStorage.getItem("eventData");
    if (eventData) {
      const eventSlug = JSON.parse(eventData).eventName;
      return eventSlug;
    }
    return "Event Name";
  }
  const eventName = getEventName();

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const effectiveMinify = isDesktop ? true : minifyNav;
  const isExpanded = !effectiveMinify || isHovered;
  const profileName = user?.name || 'Organizer';

  return (
    <nav
      className={`vertical_nav ${effectiveMinify ? "vertical_nav__minify" : ""} ${
        mobileNavOpen ? "vertical_nav__opened" : ""
      } ${isHovered ? "sidebar-hovered" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Top Brand Logo Section */}
      <Link to="/home" className="sidebar-logo-section">
        {/* Full Expanded Logo */}
        <img 
          src={isNightMode ? images.eventox_dark_logo : images.eventox_light_logo} 
          alt="Eventox Logo" 
          className="logo-expanded" 
          style={{
            opacity: isExpanded ? 1 : 0,
            transform: isExpanded ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.5)',
            pointerEvents: isExpanded ? 'auto' : 'none'
          }}
        />

        {/* Collapsed Icon/Logo */}
        <img 
          src={isNightMode ? images.white_icon : images.grey_logo} 
          alt="Eventox Collapsed Logo" 
          className="logo-collapsed" 
          style={{
            opacity: isExpanded ? 0 : 1,
            transform: isExpanded ? 'translate(-50%, -50%) scale(0.5)' : 'translate(-50%, -50%) scale(1)',
            pointerEvents: isExpanded ? 'none' : 'auto'
          }}
        />
      </Link>

      {/* Menu Items */}
      <div className="sidebar-nav-list">
        <Link
          to={`/venue-event/${eventId}/`}
          className="sidebar-btn menu--link"
          title="Dashboard"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <LayoutDashboard className="sidebar-icon" />
          <span className="sidebar-label">{eventName}</span>
        </Link>

        <Link
          to={`/venue-event/${eventId}/tickets`}
          className="sidebar-btn menu--link"
          title="Tickets"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <Ticket className="sidebar-icon" />
          <span className="sidebar-label">Tickets</span>
        </Link>

        <Link
          to={`/venue-event/${eventId}/coupons`}
          className="sidebar-btn menu--link"
          title="Promotion"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <Percent className="sidebar-icon" />
          <span className="sidebar-label">Coupons</span>
        </Link>

        <Link
          to={`/venue-event/${eventId}/reports`}
          className="sidebar-btn menu--link"
          title="Reports"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <BarChart3 className="sidebar-icon" />
          <span className="sidebar-label">Reports</span>
        </Link>

        <Link
          to={`/venue-event/${eventId}/contact-lists`}
          className="sidebar-btn menu--link"
          title="Contact List"
          onClick={() => {
            setMobileNavBarOpen(false);
          }}
        >
          <div className="active-indicator" />
          <Users className="sidebar-icon" />
          <span className="sidebar-label">Contact List</span>
        </Link>

        {hasSlotData && (
          <Link
            to={`/venue-event/${eventId}/slot`}
            className="sidebar-btn menu--link"
            title="Slot"
            onClick={() => {
              setMobileNavBarOpen(false);
            }}
          >
            <div className="active-indicator" />
            <CalendarRange className="sidebar-icon" />
            <span className="sidebar-label">Slot Bookings</span>
          </Link>
        )}
      </div>

      {/* Bottom Actions - Theme, Logout & Identity */}
      <div className="sidebar-bottom-actions">
        {/* Adaptive Theme Toggle Option */}
        <button
          id="sidebar-theme-toggle"
          onClick={toggleNightMode}
          className="sidebar-btn"
          title="Switch App Theme"
        >
          <Palette className={`sidebar-icon ${isNightMode ? 'theme-pulse-icon' : ''}`} />
          <div className="profile-info">
            <span className="sub-meta-text">App Theme</span>
            <span className="theme-name">
              {isNightMode ? 'Eventox Original' : 'Classic Gray'}
            </span>
          </div>
        </button>

        {/* Logout Link */}
        <Link
          to="/login"
          className="sidebar-btn logout-btn menu--link"
          title="Logout"
          onClick={() => {
            setMobileNavBarOpen(false);
            localStorage.clear();
          }}
        >
          <LogOut className="sidebar-icon logout-icon" />
          <span className="sidebar-label">Logout</span>
        </Link>

        {/* Organizer mini identity */}
        <div id="sidebar-mini-profile" className="sidebar-mini-profile">
          <div className="profile-avatar">
            {profileName.split(' ').map(n => n[0]).join('') || 'O'}
          </div>
          <div className="profile-info">
            <span className="profile-name">{profileName}</span>
            <span className="profile-role">Organizer</span>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default UserEventSideNavMenu;
